import { useQuery } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";
import type { DateTime } from "luxon";
import { useEffect, useMemo } from "react";
import { useAuth } from "../../../contexts/AuthProvider";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useRevealWage } from "../../../hooks/useRevealWage";
import { backendService } from "../../../services/backend.service";
import type { OtMode } from "../../../utils/models";
import { MC_QUERY_DEFAULTS } from "../utils";
import type { MultiFacilityGridResponse } from "./multiFacilityGridColumns";

/**
 * Portfolio-wide wage gate for the multi-facility view: the user must have wage revealed AND no
 * accessible facility may hide wage — the backend returns fully-empty ppd/cost responses if ANY
 * requested department is wage-hidden, so firing those queries without this gate only wastes a
 * request. Gates the ppd/cost queries and hides the four cost chart cards. Deliberately NOT
 * applied to the KPI summary: the backend blanks only its cost KPIs (avgPpd/totalCost) and still
 * returns the hour-based ones, so that query stays worth firing.
 */
export const usePortfolioWageAllowed = (): boolean => {
	const canViewWage = useRevealWage();
	const { userInfo } = useAuth();
	return canViewWage && !userInfo.departments.some((department) => department.hide_wage);
};

/**
 * Whether the money columns render at all on the network surfaces (the metrics table and the
 * performance matrix).
 *
 * Deliberately NOT `usePortfolioWageAllowed`: that one is all-or-nothing, so a single restricted
 * facility hid PPD for the entire network. Those surfaces now gate per facility — the backend zeroes
 * the restricted facilities' money values and returns everyone else's — so the only question left
 * here is whether this user may see wages at all. The cost CHARTS still use the portfolio gate,
 * because their endpoints still blank portfolio-wide.
 */
export const useNetworkWageAllowed = (): boolean => useRevealWage();

type MultiFacilityQueryArgs = {
	/** Sorted, stable order — part of the query key, so callers must not reorder between renders. */
	departmentIds: string[];
	sinceDate: DateTime;
	endDate?: DateTime;
};

/**
 * The single-mode MC settings that change VALUES, threaded through so multi numbers match the
 * single view under the same user settings (late-scheduled shifts, weekly-OT day attribution,
 * holiday-date removal). All are query-key parts.
 */
type MultiFacilitySettingsArgs = {
	includeLateScheduled?: boolean;
	otMode: OtMode;
	excludeHolidays?: boolean;
};

/**
 * Shared plumbing for the seven `/management_center/multi/*` queries: stable query key, MC query
 * defaults, fetch analytics + error tracking (mirrors the single-mode MC hooks). All seven fire on
 * entry with every accessible facility — facility toggling is pure client-side visibility.
 */
const useMultiFacilityQuery = <TResponse>({
	metric,
	departmentIds,
	sinceDate,
	endDate,
	extraKeyParts,
	enabled,
	fetcher,
}: MultiFacilityQueryArgs & {
	/** Kebab-case metric id matching the endpoint path segment (e.g. "hppd-stats"). */
	metric: string;
	extraKeyParts: (boolean | string | undefined)[];
	enabled: boolean;
	fetcher: () => Promise<AxiosResponse<TResponse>>;
}) => {
	const { track, trackError } = useAnalytics();
	const eventName = `fetch_management_center_multi_${metric.replaceAll("-", "_")}`;
	const query = useQuery({
		queryKey: [`mc-multi-${metric}`, departmentIds, sinceDate.toFormat("yyyy-MM-dd"), endDate?.toFormat("yyyy-MM-dd"), ...extraKeyParts],
		queryFn: async () => {
			track(eventName, {
				department_ids: departmentIds.join(","),
				since_date: sinceDate.toFormat("yyyy-MM-dd"),
				end_date: endDate?.toFormat("yyyy-MM-dd"),
			});
			const result = await fetcher();
			return result.data;
		},
		...MC_QUERY_DEFAULTS,
		// Opt out of the MC default `keepPreviousData`: on a range/flag change it would keep the
		// previous range's charts on screen with no loading cue for the entire multi fan-out (up to
		// 50 facilities) — stale data silently mislabeled as the new range. Undefined placeholder
		// makes a key change pend again, so the cards' skeletons/dashes show while loading.
		placeholderData: undefined,
		// The header resolves the effective end date in a post-mount effect (anchoring to the latest
		// available day), so on first render `endDate` can still be undefined. Firing then would run
		// the whole 6×N-facility fan-out twice — once without end_date and once with it — with the
		// first batch discarded. Gate until the range is settled so each query fires exactly once.
		enabled: enabled && endDate !== undefined,
	});
	const { isError, error } = query;
	useEffect(() => {
		if (isError) trackError(eventName, { error_message: error?.message });
	}, [isError, error, trackError, eventName]);
	return query;
};

export const useGetMultiFacilityPpdStats = ({
	departmentIds,
	sinceDate,
	endDate,
	isActual,
	laborCost,
	estimateMissingWage,
	includeLateScheduled,
	otMode,
	excludeHolidays,
}: MultiFacilityQueryArgs & MultiFacilitySettingsArgs & { isActual: boolean; laborCost: boolean; estimateMissingWage: boolean }) => {
	const portfolioWageAllowed = usePortfolioWageAllowed();
	return useMultiFacilityQuery({
		metric: "ppd-stats",
		departmentIds,
		sinceDate,
		endDate,
		extraKeyParts: [isActual, laborCost, estimateMissingWage, includeLateScheduled, otMode, excludeHolidays],
		enabled: departmentIds.length > 1 && portfolioWageAllowed,
		fetcher: () =>
			backendService.getMultiFacilityPpdStats({
				departmentIds,
				startDate: sinceDate,
				endDate,
				isActual,
				laborCost,
				estimateMissingWage,
				includeLateScheduled,
				otMode,
				excludeHolidays,
			}),
	});
};

export const useGetMultiFacilityHppdStats = ({
	departmentIds,
	sinceDate,
	endDate,
	isActual,
	includeLateScheduled,
	otMode,
	excludeHolidays,
}: MultiFacilityQueryArgs & MultiFacilitySettingsArgs & { isActual: boolean }) =>
	useMultiFacilityQuery({
		metric: "hppd-stats",
		departmentIds,
		sinceDate,
		endDate,
		extraKeyParts: [isActual, includeLateScheduled, otMode, excludeHolidays],
		enabled: departmentIds.length > 1,
		fetcher: () =>
			backendService.getMultiFacilityHppdStats({ departmentIds, startDate: sinceDate, endDate, isActual, includeLateScheduled, otMode, excludeHolidays }),
	});

export const useGetMultiFacilityCostStats = ({
	departmentIds,
	sinceDate,
	endDate,
	isActual,
	laborCost,
	estimateMissingWage,
	includeLateScheduled,
	otMode,
	excludeHolidays,
}: MultiFacilityQueryArgs & MultiFacilitySettingsArgs & { isActual: boolean; laborCost: boolean; estimateMissingWage: boolean }) => {
	const portfolioWageAllowed = usePortfolioWageAllowed();
	return useMultiFacilityQuery({
		metric: "cost-stats",
		departmentIds,
		sinceDate,
		endDate,
		extraKeyParts: [isActual, laborCost, estimateMissingWage, includeLateScheduled, otMode, excludeHolidays],
		enabled: departmentIds.length > 1 && portfolioWageAllowed,
		fetcher: () =>
			backendService.getMultiFacilityCostStats({
				departmentIds,
				startDate: sinceDate,
				endDate,
				isActual,
				laborCost,
				estimateMissingWage,
				includeLateScheduled,
				otMode,
				excludeHolidays,
			}),
	});
};

// `is_actual` is accepted-but-ignored by shift-pickups and disciplinary-summary (uniform BE
// contract), so these two hooks neither send it nor key on it — an actual/scheduled flip must
// not refetch data it cannot change.
export const useGetMultiFacilityShiftPickups = ({
	departmentIds,
	sinceDate,
	endDate,
	excludeAgency,
	excludeHolidays,
}: MultiFacilityQueryArgs & { excludeAgency?: boolean; excludeHolidays?: boolean }) =>
	useMultiFacilityQuery({
		metric: "shift-pickups",
		departmentIds,
		sinceDate,
		endDate,
		extraKeyParts: [excludeAgency, excludeHolidays],
		enabled: departmentIds.length > 1,
		fetcher: () => backendService.getMultiFacilityShiftPickups({ departmentIds, startDate: sinceDate, endDate, excludeAgency, excludeHolidays }),
	});

export const useGetMultiFacilityDisciplinarySummary = ({ departmentIds, sinceDate, endDate }: MultiFacilityQueryArgs) =>
	useMultiFacilityQuery({
		metric: "disciplinary-summary",
		departmentIds,
		sinceDate,
		endDate,
		extraKeyParts: [],
		enabled: departmentIds.length > 1,
		fetcher: () => backendService.getMultiFacilityDisciplinarySummary({ departmentIds, startDate: sinceDate, endDate }),
	});

export const useGetMultiFacilityKpiSummary = ({
	departmentIds,
	sinceDate,
	endDate,
	isActual,
	laborCost,
	estimateMissingWage,
	includeLateScheduled,
	otMode,
	excludeHolidays,
}: MultiFacilityQueryArgs & MultiFacilitySettingsArgs & { isActual: boolean; laborCost: boolean; estimateMissingWage: boolean }) =>
	useMultiFacilityQuery({
		metric: "kpi-summary",
		departmentIds,
		sinceDate,
		endDate,
		extraKeyParts: [isActual, laborCost, estimateMissingWage, includeLateScheduled, otMode, excludeHolidays],
		// No portfolio wage gate here (unlike ppd/cost): when any facility hides wage the BE blanks
		// only avgPpd/totalCost and still returns the hour-based KPIs, which the KPI row renders
		// (the blanked cost KPIs show as "-").
		enabled: departmentIds.length > 1,
		fetcher: () =>
			backendService.getMultiFacilityKpiSummary({
				departmentIds,
				startDate: sinceDate,
				endDate,
				isActual,
				laborCost,
				estimateMissingWage,
				includeLateScheduled,
				otMode,
				excludeHolidays,
			}),
	});

/**
 * Census-weighted weekly actual/target pairs for the performance heatmap. Facility selection is
 * deliberately presentation-only, so this loads the complete network set once and the heatmap
 * filters its visible rows client-side.
 */
export const useGetMultiFacilityPerformanceOverTime = ({
	departmentIds,
	sinceDate,
	endDate,
	isActual,
	laborCost,
	estimateMissingWage,
	includeLateScheduled,
	otMode,
	excludeHolidays,
}: MultiFacilityQueryArgs & MultiFacilitySettingsArgs & { isActual: boolean; laborCost: boolean; estimateMissingWage: boolean }) => {
	const portfolioWageAllowed = usePortfolioWageAllowed();
	return useMultiFacilityQuery({
		metric: "performance-over-time",
		departmentIds,
		sinceDate,
		endDate,
		extraKeyParts: [isActual, laborCost, estimateMissingWage, includeLateScheduled, otMode, excludeHolidays, portfolioWageAllowed],
		enabled: departmentIds.length > 1,
		fetcher: () =>
			backendService.getMultiFacilityPerformanceOverTime({
				departmentIds,
				startDate: sinceDate,
				endDate,
				isActual,
				laborCost,
				estimateMissingWage,
				includeLateScheduled,
				otMode,
				excludeHolidays,
				includePpd: portfolioWageAllowed,
			}),
	});
};

/**
 * Network-scoped ops-backoffice metrics for the multi-facility grid view. `departmentIds` is the
 * sorted network set the charts also use; the backend authorizes each against the user's department
 * access and reuses the ops-backoffice data source. Gated on a settled `endDate` (like the charts'
 * `useMultiFacilityQuery`): the header resolves the end date a tick after mount, so firing while it's
 * undefined would fetch the backend's today-default range and then refetch once the real date lands.
 */
export const useMultiFacilityGrid = ({ departmentIds, sinceDate, endDate }: MultiFacilityQueryArgs) =>
	useQuery<MultiFacilityGridResponse>({
		queryKey: ["multi-facility-grid", departmentIds.join(","), sinceDate.toISODate(), endDate?.toISODate() ?? "open"],
		queryFn: async () => {
			const { data } = await backendService.getManagementCenterMultiGrid({ departmentIds, startDate: sinceDate, endDate });
			return data;
		},
		enabled: departmentIds.length > 0 && endDate !== undefined,
		...MC_QUERY_DEFAULTS,
	});
